import React, { useState, useEffect, useRef } from "react";
import "../css/Chatpopup.css";
import chatbotIcon from "../img/chatbotIcon.jpg";
import user from "../img/user.png";
import { MdSend } from "react-icons/md";

const ChatPopup = ({ isOpen, onClose }) => {
  // Messaggi iniziali da sessionStorage o messaggio di benvenuto
  const [messages, setMessages] = useState(() => {
    const storedMessages = sessionStorage.getItem("chatMessages");
    return storedMessages
      ? JSON.parse(storedMessages)
      : [
          {
            sender: "assistant",
            text:
              "Hello! I'm your AI Assistant, here to help you with any questions or issues regarding our platform. Whether you need guidance navigating our features or assistance with a specific task, I'm ready to support you. How can I help you today?",
          },
        ];
  });

  const [inputMessage, setInputMessage] = useState("");
  const [isClosing, setIsClosing] = useState(false);

  const chatAreaRef = useRef(null);

  // === FIX SCROLL: autoscroll solo se l’utente è vicino al fondo
  const [autoScroll, setAutoScroll] = useState(true);
  const messagesRef = useRef(messages);
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  // Carica dallo storage quando si apre
  useEffect(() => {
    if (isOpen) {
      const stored = sessionStorage.getItem("chatMessages");
      if (stored) setMessages(JSON.parse(stored));
    }
  }, [isOpen]);

  // Salva sempre su storage quando cambia
  useEffect(() => {
    sessionStorage.setItem("chatMessages", JSON.stringify(messages));
  }, [messages]);

  // Listener scroll per capire se sei al fondo
  useEffect(() => {
    const el = chatAreaRef.current;
    if (!el) return;

    const onScroll = () => {
      const threshold = 40; // px di tolleranza
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
      setAutoScroll(atBottom);
    };

    el.addEventListener("scroll", onScroll, { passive: true });

    // all’apertura porta subito in fondo
    el.scrollTop = el.scrollHeight;

    return () => el.removeEventListener("scroll", onScroll);
  }, [isOpen]);

  // Autoscroll solo se autoScroll === true
  useEffect(() => {
    if (!chatAreaRef.current) return;
    if (autoScroll) {
      chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
    }
  }, [messages, autoScroll]);

  // Polling storage: aggiorna stato solo se è davvero cambiato
  useEffect(() => {
    const intervalId = setInterval(() => {
      const raw = sessionStorage.getItem("chatMessages");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const same = JSON.stringify(parsed) === JSON.stringify(messagesRef.current);
      if (!same) setMessages(parsed);
    }, 500);
    return () => clearInterval(intervalId);
  }, []);

  // ====== MOBILE UX helpers ======

  // 1) Fallback per 100dvh: aggiorna --vh con window.innerHeight
  useEffect(() => {
    const setVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };
    setVh();
    window.addEventListener("resize", setVh);
    window.addEventListener("orientationchange", setVh);
    return () => {
      window.removeEventListener("resize", setVh);
      window.removeEventListener("orientationchange", setVh);
    };
  }, []);

  // 2) Blocca lo scroll del body quando la chat è aperta
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  // 3) Assicura che il textarea sia visibile quando appare la tastiera
  useEffect(() => {
    const el = document.querySelector(".chat-input textarea");
    if (!el) return;
    const onFocus = () => {
      setTimeout(() => {
        el.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }, 100);
    };
    el.addEventListener("focus", onFocus);
    return () => el.removeEventListener("focus", onFocus);
  }, []);

  // ====== Helpers ======
  const buildHistory = (msgs) =>
    msgs
      .filter(
        (m) =>
          m &&
          typeof m.text === "string" &&
          m.text.trim() !== "" &&
          m.text !== "Assistant is typing..."
      )
      .map((m) => ({
        role: m.sender === "user" ? "user" : "assistant",
        content: m.text,
      }));

  const sendMessage = async () => {
    if (inputMessage.trim() === "") return;

    // all’invio riabilita l’autoscroll
    setAutoScroll(true);

    const userMessage = { sender: "user", text: inputMessage };
    const currentInput = inputMessage;
    setInputMessage("");

    const historyPayload = buildHistory([...messages, userMessage]);

    setMessages((prev) => [
      ...prev,
      userMessage,
      { sender: "assistant", text: "Assistant is typing..." },
    ]);

    try {
      const response = await fetch("https://adapt-flask-ep.com/adapt/ws.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          serviceName: "chatbot_SM",
          history: historyPayload,
          message: currentInput,
          stream: false,
        }),
      });

      const responseData = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(
          `HTTP ${response.status}: ${responseData?.reason || "Request failed"}`
        );
      }

      const botText =
        responseData?.output?.message ??
        responseData?.output ??
        responseData?.message ??
        responseData?.reply ??
        (typeof responseData === "string" ? responseData : JSON.stringify(responseData));

      const assistantMessage = {
        sender: "assistant",
        text: typeof botText === "string" ? botText : JSON.stringify(botText),
      };

      setMessages((prev) => {
        const next = [...prev];
        if (next.length && next[next.length - 1].text === "Assistant is typing...") {
          next.pop();
        }
        return [...next, assistantMessage];
      });
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages((prev) => {
        const next = [...prev];
        if (next.length && next[next.length - 1].text === "Assistant is typing...") {
          next.pop();
        }
        return [
          ...next,
          {
            sender: "assistant",
            text:
              "Sorry, there was an error processing your request." +
              (error?.message ? ` (${error.message})` : ""),
          },
        ];
      });
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    const initialMessage = {
      sender: "assistant",
      text:
        "Hello! I'm your AI Assistant, here to help you with any questions or issues regarding our platform. Whether you need guidance navigating our features or assistance with a specific task, I'm ready to support you. How can I help you today?",
    };
    setMessages([initialMessage]);
    sessionStorage.removeItem("chatMessages");
  };

  const formatMessage = (message) => {
    let formattedText = message
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/(?:\r\n|\r|\n)/g, "<br />")
      .replace(/^\d+\.\s(.*?)$/gm, "<li>$1</li>")
      .replace(/^- (.*?)$/gm, "<li>$1</li>");
    if (formattedText.includes("<li>")) {
      formattedText = formattedText.replace(/(<li>.*?<\/li>)/g, "<ul>$1</ul>");
    }
    return formattedText;
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 800);
  };

  if (!isOpen && !isClosing) return null;

  return (
    <div className="popup-overlay">
      <div className={`big-popup ${isClosing ? "exit" : ""}`}>
        <div className="popup-header">
          <div className="popup-header-inner">
            <img src={chatbotIcon} alt="Chatbot" className="chatbot-icon-message" />
            <h2 className="titoloAI">AI Assistant</h2>
          </div>
          <div>
            <button className="button clear-button" onClick={clearChat}>
              Clear Chat
            </button>
            <button className="button close-button" onClick={handleClose}>
              Close chat
            </button>
          </div>
        </div>

        <div className="chat-area" ref={chatAreaRef}>
          {messages.map((message, index) => (
            <React.Fragment key={index}>
              <div className="space"></div>
              <div
                className={`message ${
                  message.sender === "user" ? "user-message-group" : "assistant-message-group"
                }`}
              >
                {message.sender === "assistant" && (
                  <img src={chatbotIcon} alt="Chatbot" className="chatbot-icon-message" />
                )}
                <div
                  className={`chat-message ${
                    message.sender === "user" ? "user-message" : "assistant-message"
                  }`}
                  dangerouslySetInnerHTML={{ __html: formatMessage(message.text) }}
                />
                {message.sender === "user" && (
                  <img src={user} alt="User" className="user-icon-message" />
                )}
              </div>
            </React.Fragment>
          ))}
        </div>

        <div className="chat-body">
          <label>Welcome! Write your message here.</label>
          <div className="chat-input">
            <textarea
              placeholder="Write a message..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={handleKeyPress}
            />
            <button className="button" onClick={sendMessage} aria-label="Send message">
              <MdSend size={25} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatPopup;
